import type {
  EmailLinkFactor,
  PrepareFirstFactorParams,
  SetActive,
  SignInResource,
  SignInStartEmailLinkFlowParams,
  SignUpResource,
  StartEmailLinkFlowParams as SignUpStartEmailLinkFlowParams,
} from '@clerk/types'

import { type ClerkAPIResponseError } from '@clerk/shared'

import type { Dispatch, SetStateAction } from 'react'

import isArray from 'lodash/fp/isArray'
import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNil from 'lodash/fp/isNil'
import isString from 'lodash/fp/isString'
import omit from 'lodash/fp/omit'

import type { CoreUser, CoreUserRoles, HTTPError } from '../types'

import { MAGIC_LINK_RESEND_DELAY } from '../constants'
import { Apps, Plans, Roles } from '../enums'
import { Logger } from './log'

export const CLERK_SUPABASE_JWT_TEMPLATE = 'Supabase'

export function isPublicPage(
  pathname: string | undefined,
  publicPages: string[] | undefined,
  {
    context = {},
    logger,
    message,
  }: {
    context?: { error?: Error } & Record<string, unknown>
    logger?: Logger
    message?: string
  } = {},
) {
  if (
    !isEmpty(pathname) &&
    isArray(publicPages) &&
    !publicPages.includes(pathname)
  ) {
    const error = new Error('Unauthorized') as HTTPError
    error.status = 401

    if (!isNil(logger) && isFunction(logger.error)) {
      logger.error(
        `[USER][SERVER] Unable to load the user${!isEmpty(message) ? `: "${message}"` : ''}`,
        omit(['error'], context),
        context?.error,
      )
    }

    throw error
  }
}

type SigninParams = {
  clerkSignin: SignInResource | undefined
  email: string
  isLoaded: boolean
  logger: Logger
  setIsExpired:
    | ((newValue: boolean) => void)
    | Dispatch<SetStateAction<boolean>>
  setIsVerifying:
    | ((newValue: boolean) => void)
    | Dispatch<SetStateAction<boolean>>
  startEmailLinkFlow:
    | ((params: SignInStartEmailLinkFlowParams) => Promise<SignInResource>)
    | undefined
  postSigninUrl?: string
  setActive?: SetActive
  setUnknownUserError?: Dispatch<SetStateAction<boolean>>
}
export async function signin({
  clerkSignin,
  email,
  isLoaded,
  logger,
  postSigninUrl,
  setActive,
  setIsExpired,
  setIsVerifying,
  setUnknownUserError,
  startEmailLinkFlow,
}: SigninParams) {
  if (
    !isLoaded ||
    isNil(clerkSignin) ||
    !isFunction(clerkSignin?.create) ||
    !isFunction(startEmailLinkFlow)
  ) {
    logger.error(`Clerk is not loaded`, {
      isLoaded,
      signin: clerkSignin,
    })

    return false
  }

  setIsExpired(false)

  const signinParams = { identifier: email }
  let signinFlow
  try {
    signinFlow = await clerkSignin.create(signinParams)

    if (isEmpty(signinFlow)) {
      throw new Error('Signin flow is empty')
    }
  } catch (signinError) {
    const actualError = signinError as Error
    logger.error(
      `Unable to create signin: "${actualError.message}"`,
      {
        params: signinParams,
      },
      actualError,
    )

    if (
      (signinError as ClerkAPIResponseError).errors.some(
        ({ code }) => code === 'form_identifier_not_found',
      )
    ) {
      setUnknownUserError?.(true)
    }

    return false
  }

  setIsVerifying(true)

  const { emailAddressId } = (signinFlow.supportedFirstFactors?.find(
    (firstFactor) =>
      firstFactor.strategy === 'email_link' &&
      firstFactor.safeIdentifier === email,
  ) ?? { emailAddressId: '' }) as EmailLinkFactor

  if (isEmpty(emailAddressId)) {
    logger.error(`No user matching '${email}'`, { email })

    return false
  }

  let returnUrl =
    isEmpty(postSigninUrl) ||
    postSigninUrl === 'undefined' ||
    postSigninUrl === 'null' ||
    !isString(postSigninUrl)
      ? undefined
      : postSigninUrl
  if (
    !isEmpty(returnUrl) &&
    !returnUrl.startsWith('http') &&
    !returnUrl.startsWith('/')
  ) {
    returnUrl = `/${returnUrl}`
  }

  let signinResult: SignInResource
  const emailLinkParams = {
    emailAddressId,
    redirectUrl: returnUrl?.startsWith('http')
      ? returnUrl
      : `${window.location.origin}${returnUrl || '/'}`,
  }
  try {
    // Start the magic link flow.
    // Pass the app URL that users will be navigated when they click the magic
    // link from their email inbox.
    signinResult = await startEmailLinkFlow(emailLinkParams)
  } catch (emailLinkError) {
    const actualError = emailLinkError as Error
    logger.error(
      `Unable to start magic link flow: "${actualError.message}"`,
      {
        params: emailLinkParams,
      },
      actualError,
    )

    return false
  }

  // Check the verification result.
  const verification = signinResult.firstFactorVerification
  if (verification.verifiedFromTheSameClient()) {
    return true
  }

  if (verification.status === 'expired') {
    setIsVerifying(true)
    setIsExpired(true)

    return false
  }

  if (signinResult.status === 'complete') {
    try {
      await setActive?.({ session: signinResult.createdSessionId })
    } catch (setActiveError) {
      const actualError = setActiveError as Error
      logger.warn(
        `Unable to activate the newly created session ${actualError.message}`,
        {},
        actualError,
      )
    }

    return true
  }

  logger.error(`Signin flow didn't complete`, {
    signinResult,
  })

  return false
}

type SignupParams = {
  email: string
  logger: Logger
  redirect: (path: string) => void
  setIsExpired:
    | ((newValue: boolean) => void)
    | Dispatch<SetStateAction<boolean>>
  setIsVerifying:
    | ((newValue: boolean) => void)
    | Dispatch<SetStateAction<boolean>>
  signin: {
    clerkSignin: SignInResource | undefined
    isLoaded: boolean
    startEmailLinkFlow:
      | ((params: SignInStartEmailLinkFlowParams) => Promise<SignInResource>)
      | undefined
    setActive?: SetActive
  }
  signup: {
    clerkSignup: SignUpResource | undefined
    isLoaded: boolean
    register: (email: string) => Promise<void>
    prepareEmailAddressVerification?: SignUpResource['prepareEmailAddressVerification']
    setActive?: SetActive
    startEmailLinkFlow?:
      | ((params: SignUpStartEmailLinkFlowParams) => Promise<SignUpResource>)
      | undefined
    ticket?: string
  }
  postSignupUrl?: string
  setAllowResend?: Dispatch<SetStateAction<boolean>>
}
export async function signup({
  email,
  logger,
  postSignupUrl = '/',
  redirect,
  setAllowResend,
  setIsExpired,
  setIsVerifying,
  signin: {
    clerkSignin,
    isLoaded: isSigninLoaded,
    setActive: setSigninActive,
    startEmailLinkFlow: startSigninEmailLinkFlow,
  },
  signup: {
    clerkSignup,
    isLoaded: isSignupLoaded,
    prepareEmailAddressVerification,
    register: registerUser,
    setActive: setSignupActive,
    startEmailLinkFlow: startSignupEmailLinkFlow,
    ticket,
  },
}: SignupParams) {
  if (
    !isSignupLoaded ||
    isNil(clerkSignup) ||
    !isFunction(clerkSignup?.create)
  ) {
    logger.error(`Clerk is not loaded`, {
      isLoaded: isSignupLoaded,
      isMagicLink: isFunction(startSignupEmailLinkFlow),
      signup: clerkSignup,
    })

    return { error: new Error('Clerk is not loaded'), result: false }
  }

  if (isFunction(setAllowResend)) {
    setTimeout(() => {
      setAllowResend(true)
    }, MAGIC_LINK_RESEND_DELAY)
  }

  const signupParams = isEmpty(ticket)
    ? { emailAddress: email }
    : { strategy: 'ticket' as const, ticket }

  let signupResult:
    | Awaited<
        | ReturnType<Exclude<typeof prepareEmailAddressVerification, undefined>>
        | ReturnType<Exclude<typeof startSignupEmailLinkFlow, undefined>>
        | ReturnType<typeof clerkSignup.create>
      >
    | undefined

  try {
    // Start the sign up flow, by collecting the user's email address.
    signupResult = await clerkSignup.create(signupParams)
  } catch (signupError) {
    if (
      (signupError as ClerkAPIResponseError).errors.some(
        ({ code }) => code === 'form_identifier_exists',
      )
    ) {
      logger.warn(
        `Unable to create signup because the user '${email}' is already registered`,
        {
          isInvitation: !isEmpty(ticket),
          isMagicLink: isFunction(startSignupEmailLinkFlow) && isEmpty(ticket),
          params: signupParams,
        },
        signupError as Error,
      )

      const signinResult = await signin({
        clerkSignin,
        email,
        isLoaded: isSigninLoaded,
        logger,
        postSigninUrl: postSignupUrl,
        setActive: setSigninActive,
        setIsExpired,
        setIsVerifying,
        startEmailLinkFlow: startSigninEmailLinkFlow,
      })

      if (!signinResult) {
        logger.warn(
          `Unable to automatically signin the existing user, redirecting them to the signin page`,
        )

        return redirect('/signin?message=signup-existing-user')
      }

      return signinResult
    }

    const actualError = signupError as Error
    logger.error(
      `Unable to create signup: "${actualError.message}"`,
      {
        isInvitation: !isEmpty(ticket),
        isMagicLink: isFunction(startSignupEmailLinkFlow) && isEmpty(ticket),
        params: signupParams,
      },
      actualError,
    )

    return { error: actualError, result: false }
  }

  const emailLinkParams: Partial<SignUpStartEmailLinkFlowParams> = {}

  if (isEmpty(ticket)) {
    try {
      await registerUser(email)
    } catch (registerUserError) {
      const actualError = registerUserError as Error
      logger.warn(
        `Unable to register user: "${actualError.message}"`,
        {
          email,
        },
        actualError,
      )
    }

    const defaultRedirectUrl = `${window.location.origin}/`
    let redirectUrl =
      (isEmpty(postSignupUrl) ||
      postSignupUrl === 'undefined' ||
      postSignupUrl === 'null' ||
      !isString(postSignupUrl)
        ? defaultRedirectUrl
        : postSignupUrl) || defaultRedirectUrl
    if (!redirectUrl.startsWith('http') && !redirectUrl.startsWith('/')) {
      redirectUrl = `/${redirectUrl}`
    }
    if (!redirectUrl.startsWith('http')) {
      redirectUrl = `${window.location.origin}${redirectUrl}`
    }

    emailLinkParams.redirectUrl = redirectUrl

    if (
      isFunction(startSignupEmailLinkFlow) ||
      isFunction(prepareEmailAddressVerification)
    ) {
      try {
        setIsVerifying(true)
        setIsExpired(false)

        /* eslint-disable line-comment-position */
        signupResult = isFunction(startSignupEmailLinkFlow)
          ? // Start the magic link flow.
            // Pass the app URL that users will be navigated when they click the magic
            // link from their email inbox.
            await startSignupEmailLinkFlow(
              emailLinkParams as SignUpStartEmailLinkFlowParams,
            )
          : // Start the email verification flow.
            // Pass the app URL that users will be navigated when they click the magic
            // link from their email inbox.
            await prepareEmailAddressVerification?.({
              strategy: 'email_link',
              ...(emailLinkParams as SignUpStartEmailLinkFlowParams),
            })
        /* eslint-enable line-comment-position */
      } catch (emailLinkError) {
        setIsVerifying(false)
        setIsExpired(false)

        const actualError = emailLinkError as Error
        logger.error(
          `Unable to start magic link or email verification flow: "${actualError.message}"`,
          {
            isMagicLink: isFunction(startSignupEmailLinkFlow),
            params: emailLinkParams,
          },
          actualError,
        )

        return { error: actualError, result: false }
      }
    } else {
      return null
    }
  }

  if (isEmpty(signupResult)) {
    const actualError = new Error('Signup result is empty!')
    logger.error(
      `Unable to ${isEmpty(ticket) ? 'start magic link or email verification flow' : 'accept invitation'}: "${actualError.message}"`,
      {
        isInvitation: !isEmpty(ticket),
        isMagicLink: isFunction(startSignupEmailLinkFlow) && isEmpty(ticket),
        params: emailLinkParams,
      },
      actualError,
    )

    return { error: actualError, result: false }
  }

  if (isEmpty(ticket)) {
    // Check the verification result.
    const verification = signupResult.verifications.emailAddress
    if (verification.verifiedFromTheSameClient()) {
      return true
    }

    if (verification.status === 'expired') {
      setIsVerifying(false)
      setIsExpired(true)
    }
  }

  if (signupResult.status === 'complete') {
    setIsVerifying(false)
    setIsExpired(false)

    try {
      await setSignupActive?.({ session: signupResult.createdSessionId })
    } catch (setActiveError) {
      const actualError = setActiveError as Error
      logger.warn(
        `Unable to activate the newly created session ${actualError.message}`,
        {
          isMagicLink: isFunction(startSignupEmailLinkFlow),
          params: emailLinkParams,
          signupResult,
        },
        actualError,
      )
    }

    return true
  }

  const actualError = new Error("Signup flow didn't complete")
  logger.error(
    actualError.message,
    {
      isMagicLink: isFunction(startSignupEmailLinkFlow),
      signupResult,
    },
    actualError,
  )

  return { error: actualError, result: false }
}

type SigninWithOTPParams = {
  clerkSignin: SignInResource | undefined
  email: string
  isLoaded: boolean
  logger: Logger
  pinCode: string
  setIsExpired: Dispatch<SetStateAction<boolean>>
  setIsVerifying: Dispatch<SetStateAction<boolean>>
  setActive?: SetActive
  setUnknownUserError?: Dispatch<SetStateAction<boolean>>
}
/** @public */
export async function signinWithOTP({
  clerkSignin,
  email,
  isLoaded,
  logger,
  pinCode,
  setActive,
  setIsExpired,
  setIsVerifying,
  setUnknownUserError,
}: SigninWithOTPParams) {
  if (!isLoaded || isNil(clerkSignin) || !isFunction(clerkSignin?.create)) {
    logger.error(`Clerk is not loaded`, {
      isLoaded,
      signin: clerkSignin,
    })

    return false
  }

  setIsExpired(false)

  const signinParams = { identifier: email }
  let signinFlow
  try {
    signinFlow = await clerkSignin.create(signinParams)

    if (isEmpty(signinFlow)) {
      throw new Error('Signin flow is empty')
    }
  } catch (signinError) {
    const actualError = signinError as Error
    logger.error(
      `Unable to create signin: "${actualError.message}"`,
      {
        params: signinParams,
      },
      actualError,
    )

    if (
      (signinError as ClerkAPIResponseError).errors.some(
        ({ code }) => code === 'form_identifier_not_found',
      )
    ) {
      setUnknownUserError?.(true)
    }

    return false
  }

  setIsVerifying(true)

  const { emailAddressId } = (signinFlow.supportedFirstFactors?.find(
    (firstFactor) =>
      firstFactor.strategy === 'email_code' &&
      firstFactor.safeIdentifier === email,
  ) ?? { emailAddressId: '' }) as EmailLinkFactor

  if (isEmpty(emailAddressId)) {
    logger.error(`No user matching '${email}'`, { email })

    return false
  }

  const otpParams: PrepareFirstFactorParams = {
    emailAddressId,
    strategy: 'email_code',
  }
  try {
    await clerkSignin.prepareFirstFactor(otpParams)
  } catch (otpStartError) {
    const actualError = otpStartError as Error
    logger.error(
      `Unable to start signin OTP verification: "${actualError.message}"`,
      {
        params: otpParams,
      },
      actualError,
    )

    return false
  }

  let signinResult
  try {
    signinResult = await clerkSignin.attemptFirstFactor({
      code: pinCode,
      strategy: 'email_code',
    })
  } catch (otpError) {
    const actualError = otpError as Error
    logger.error(
      `Unable to verify signin OTP: "${actualError.message}"`,
      {},
      actualError,
    )

    return false
  }

  // Check the verification result.
  const verification = signinResult.firstFactorVerification
  if (verification.verifiedFromTheSameClient()) {
    return true
  }

  if (verification.status === 'expired') {
    setIsExpired(true)

    return false
  }

  if (signinResult.status === 'complete') {
    try {
      await setActive?.({ session: signinResult.createdSessionId })
    } catch (setActiveError) {
      const actualError = setActiveError as Error
      logger.warn(
        `Unable to activate the newly created session ${actualError.message}`,
        {},
        actualError,
      )
    }

    return true
  }

  logger.error(`Signin flow didn't complete`, {
    signinResult,
  })

  return false
}

type SignupWithOTPParams = {
  email: string
  logger: Logger
  pinCode: string
  redirect: (path: string) => void
  setAllowResend: Dispatch<SetStateAction<boolean>>
  setIsExpired: Dispatch<SetStateAction<boolean>>
  setIsVerifying: Dispatch<SetStateAction<boolean>>
  signin: {
    clerkSignin: SignInResource | undefined
    isLoaded: boolean
    setActive?: SetActive
  }
  signup: {
    clerkSignup: SignUpResource | undefined
    isLoaded: boolean
    register: (email: string) => Promise<void>
    setActive?: SetActive
  }
}
export async function signupWithOTP({
  email,
  logger,
  pinCode,
  redirect,
  setAllowResend,
  setIsExpired,
  setIsVerifying,
  signin: { clerkSignin, isLoaded: isSigninLoaded, setActive: setSigninActive },
  signup: {
    clerkSignup,
    isLoaded: isSignupLoaded,
    register: registerUser,
    setActive: setSignupActive,
  },
}: SignupWithOTPParams) {
  if (
    !isSignupLoaded ||
    isNil(clerkSignup) ||
    !isFunction(clerkSignup?.create)
  ) {
    logger.error(`Clerk is not loaded`, {
      isLoaded: isSignupLoaded,
      signup: clerkSignup,
    })

    return false
  }

  setTimeout(() => {
    setAllowResend(true)
  }, MAGIC_LINK_RESEND_DELAY)

  setIsVerifying(true)
  setIsExpired(false)

  const signupParams = { emailAddress: email }
  try {
    // Start the sign up flow, by collecting the user's email address.
    await clerkSignup.create(signupParams)
  } catch (signupError) {
    if (
      (signupError as ClerkAPIResponseError).errors.some(
        ({ code }) => code === 'form_identifier_exists',
      )
    ) {
      logger.warn(
        `Unable to create signup because the user '${email}' is already registered`,
        {
          error: signupError,
          params: signupParams,
        },
      )

      const signinResult = await signinWithOTP({
        clerkSignin,
        email,
        isLoaded: isSigninLoaded,
        logger,
        pinCode,
        setActive: setSigninActive,
        setIsExpired,
        setIsVerifying,
      })

      if (!signinResult) {
        logger.warn(
          `Unable to signin the existing user, redirecting them to the signin page`,
        )

        return redirect('/signin?message=signup-existing-user')
      }

      return false
    }

    const actualError = signupError as Error
    logger.error(
      `Unable to create signup: "${actualError.message}"`,
      {
        params: signupParams,
      },
      actualError,
    )

    return false
  }

  try {
    await registerUser(email)
  } catch (registerUserError) {
    const actualError = registerUserError as Error
    logger.warn(
      `Unable to register user: "${actualError.message}"`,
      {
        email,
      },
      actualError,
    )
  }

  try {
    // Start the OTP code verification process.
    await clerkSignup.prepareEmailAddressVerification()
  } catch (otpStartError) {
    const actualError = otpStartError as Error
    logger.error(
      `Unable to start signup OTP verification: "${actualError.message}"`,
      {},
      actualError,
    )

    return false
  }

  let signupResult
  try {
    signupResult = await clerkSignup.attemptEmailAddressVerification({
      code: pinCode,
    })
  } catch (otpError) {
    const actualError = otpError as Error
    logger.error(
      `Unable to verify signup OTP: "${actualError.message}"`,
      {},
      actualError,
    )

    return false
  }

  const verification = signupResult.verifications.emailAddress
  if (verification.verifiedFromTheSameClient()) {
    return true
  }

  if (verification.status === 'expired') {
    setIsExpired(true)

    return false
  }

  if (signupResult.status === 'complete') {
    try {
      await setSignupActive?.({ session: signupResult.createdSessionId })
    } catch (setActiveError) {
      const actualError = setActiveError as Error
      logger.warn(
        `Unable to activate the newly created session ${actualError.message}`,
        {},
        actualError,
      )
    }

    return true
  }

  logger.error(`Signup flow didn't complete`, {
    signupResult,
  })

  return false
}
export type UserForRole = {
  role?: string | Roles | null
  roles?: CoreUserRoles
} & CoreUser
export type BaseRoleFlags = {
  isActuallyAdmin: boolean
  isAdmin: boolean
  isCoach: boolean
  isCorrector: boolean
  isCreator: boolean
  isMember: boolean
  isStaff: boolean
  role: string | Roles | null
}
export type RoleFlags = BaseRoleFlags &
  Partial<Record<'max' | 'media', Omit<BaseRoleFlags, 'isActuallyAdmin'>>>
type GetRoleFlagsReturn = RoleFlags
export function getRoleFlags<T extends UserForRole>(
  roleOrUser?: string | Roles | T | null,
  searchParams?: URLSearchParams,
): GetRoleFlagsReturn {
  let role: string | Roles | null = isString(roleOrUser)
    ? roleOrUser
    : (roleOrUser?.role ?? Roles.Member)

  let isActuallyAdmin = false
  let searchParamsToUse = searchParams
  if (!isNil(searchParams) && searchParams?.size > 0) {
    const originalRoleFlags = getRoleFlags(roleOrUser)
    isActuallyAdmin = originalRoleFlags.isActuallyAdmin
    if (!isActuallyAdmin) {
      searchParamsToUse = undefined
    }
  }

  const actualRoles: Record<Apps, 'guest' | Roles | null> = {
    [Apps.Creators]: searchParamsToUse?.get('mediaRole') as Roles,
    [Apps.Igloo]: null,
    [Apps.Max]: searchParamsToUse?.get('maxRole') as Roles,
    [Apps.Media]: searchParamsToUse?.get('mediaRole') as Roles,
  }

  if (searchParamsToUse?.has('asRole')) {
    switch (searchParamsToUse?.get('asRole')) {
      case 'guest': {
        actualRoles[Apps.Max] = Roles.Member
        actualRoles[Apps.Media] = Roles.Member
        role = Roles.Member
        break
      }
      case 'max': {
        actualRoles[Apps.Max] = Roles.Member
        actualRoles[Apps.Media] = Roles.Member
        role = Roles.Member
        break
      }

      case Plans.Free: {
        actualRoles[Apps.Max] = Roles.Member
        actualRoles[Apps.Media] = Roles.Member
        role = Roles.Member
        break
      }

      case 'plus': {
        actualRoles[Apps.Max] = Roles.Member
        actualRoles[Apps.Media] = Roles.Member
        role = Roles.Member
        break
      }

      case Roles.Admin:
      case Roles.Member: {
        actualRoles[Apps.Max] = searchParamsToUse?.get('asRole') as Roles
        actualRoles[Apps.Media] = searchParamsToUse?.get('asRole') as Roles
        role = searchParamsToUse?.get('asRole') as Roles
        break
      }

      case Roles.Coach: {
        actualRoles[Apps.Max] = Roles.Coach
        actualRoles[Apps.Media] = Roles.Member
        role = Roles.Coach
        break
      }

      case Roles.Corrector:
      case Roles.Creator: {
        actualRoles[Apps.Max] = Roles.Member
        actualRoles[Apps.Media] = searchParamsToUse?.get('asRole') as Roles
        role = searchParamsToUse?.get('asRole') as Roles
        break
      }

      default: {
        break
      }
    }
  }

  const isAdmin =
    !isNil(actualRoles[Apps.Max]) || !isNil(actualRoles[Apps.Media])
      ? actualRoles[Apps.Max] === Roles.Admin &&
        actualRoles[Apps.Media] === Roles.Admin
      : role === Roles.Admin
  if (isNil(searchParams) || searchParams?.size === 0) {
    isActuallyAdmin = isAdmin
  }

  const currentRoleFlags: BaseRoleFlags = isEmpty(role)
    ? {
        isActuallyAdmin,
        isAdmin: false,
        isCoach: false,
        isCorrector: false,
        isCreator: false,
        isMember: false,
        isStaff: false,
        role,
      }
    : {
        isActuallyAdmin,
        isAdmin,
        isCoach: isNil(actualRoles[Apps.Max])
          ? role === Roles.Coach
          : actualRoles[Apps.Max] === Roles.Coach,
        isCorrector: isNil(actualRoles[Apps.Media])
          ? role === Roles.Corrector
          : actualRoles[Apps.Media] === Roles.Corrector,
        isCreator: isNil(actualRoles[Apps.Media])
          ? role === Roles.Creator
          : actualRoles[Apps.Media] === Roles.Creator,
        isMember:
          !isNil(actualRoles[Apps.Max]) || !isNil(actualRoles[Apps.Media])
            ? actualRoles[Apps.Max] === Roles.Member &&
              actualRoles[Apps.Media] === Roles.Member
            : role === Roles.Member || isEmpty(role) || isNil(role),
        isStaff: false,
        role:
          role || (searchParamsToUse?.get('asRole') as Roles) || Roles.Member,
      }

  currentRoleFlags.isStaff =
    currentRoleFlags.isAdmin ||
    currentRoleFlags.isCoach ||
    currentRoleFlags.isCorrector ||
    currentRoleFlags.isCreator

  if (
    isString(roleOrUser) ||
    isEmpty(roleOrUser) ||
    isEmpty(roleOrUser?.roles)
  ) {
    return currentRoleFlags
  }

  return {
    ...currentRoleFlags,
    [Apps.Media]: {
      isAdmin: false,
      isCoach: false,
      isCorrector: false,
      isCreator: false,
      isMember: true,
      isStaff: false,
      role: Roles.Member,
    },
    ...roleOrUser.roles?.reduce(
      (accumulator, { app, role: roleInApp }) => ({
        ...accumulator,
        [app]: {
          isAdmin: (actualRoles[app] || roleInApp) === Roles.Admin,
          isCoach: (actualRoles[app] || roleInApp) === Roles.Coach,
          isCorrector: (actualRoles[app] || roleInApp) === Roles.Corrector,
          isCreator: (actualRoles[app] || roleInApp) === Roles.Creator,
          isMember: (actualRoles[app] || roleInApp) === Roles.Member,
          isStaff:
            (actualRoles[app] || roleInApp) === Roles.Admin ||
            (actualRoles[app] || roleInApp) === Roles.Coach ||
            (actualRoles[app] || roleInApp) === Roles.Corrector ||
            (actualRoles[app] || roleInApp) === Roles.Creator,
          role:
            actualRoles[app] === 'guest'
              ? Roles.Member
              : actualRoles[app] || roleInApp,
        },
      }),
      {} as Record<Apps, RoleFlags>,
    ),
  }
}

export function isStaff(role: string | Roles | null | undefined) {
  return (
    role === Roles.Admin ||
    role === Roles.Coach ||
    role === Roles.Corrector ||
    role === Roles.Creator
  )
}

export async function handleGoogleSignup(
  clerkSignup?: SignInResource | SignUpResource,
  redirectUrl: string = '/',
  searchParams: URLSearchParams = new URLSearchParams(),
  metadata: Record<string, string | null | undefined> = {},
) {
  if (!clerkSignup) {
    return
  }

  const fromEditionUuid = searchParams?.get('from') || undefined
  const utmData: Record<string, string> = {}
  searchParams.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      utmData[key.replace('utm_', '')] = value
    }
  })

  clerkSignup?.authenticateWithRedirect({
    continueSignUp: true,
    redirectUrl: '/signup/oauth',
    redirectUrlComplete: redirectUrl?.startsWith('http')
      ? redirectUrl
      : `${window.location.origin}${redirectUrl || '/'}`,
    strategy: 'oauth_google',
    unsafeMetadata: {
      ...metadata,
      editionId: metadata.editionId || fromEditionUuid,
      origin: metadata.origin || 'free_subscribe',
      source: metadata.source || Plans.Free,
      utmData: metadata.utmData || utmData,
    },
  })
}
